import { differenceBy, minBy } from 'lodash-es';

import { CandidateFilterType } from '@pflegia/types/api/candidate/candidate-filter-type';
import { CandidatePriority } from '@pflegia/types/api/candidate/candidate-priority';
import { MAX_DEPARTMENTS_IN_PROFILE, MAX_LOCATIONS_IN_PROFILE } from '@pflegia/types/api/candidate/constants';
import { MAX_JOB_TITLE_ANSWERS } from '@pflegia/types/api/candidate/job-title';
import { TYPES_OF_WORK_WITH_DEPARTMENTS } from '@pflegia/types/api/candidate/type-of-work';
import { FilterValue } from '@pflegia/types/api/job-board/filter-value';
import { LocationObject } from '@pflegia/types/common/location/location-object';
import { hasCommonElement } from '@pflegia/util/common/common-element';

export class FiltersDto {
  private locationFilters: FilterValue[] = [];

  private positionFilters: FilterValue[] = [];

  private facilityFilters: FilterValue[] = [];

  private departmentFilters: FilterValue[] = [];

  private employerFilters: FilterValue[] = [];

  private typeOfJobFilters: FilterValue[] = [];

  private jobPositionTitleFilters: FilterValue[] = [];

  static create(filters: FilterValue[]): FiltersDto {
    const filtersDto = new FiltersDto();
    filtersDto.locationFilters = filters.filter(item => item.type === CandidateFilterType.Location);
    filtersDto.positionFilters = filters.filter(item => item.type === CandidateFilterType.Position);
    filtersDto.facilityFilters = filters.filter(item => item.type === CandidateFilterType.Facility);
    filtersDto.departmentFilters = filters.filter(item => item.type === CandidateFilterType.Department);
    filtersDto.employerFilters = filters.filter(item => item.type === CandidateFilterType.Employment);
    filtersDto.typeOfJobFilters = filters.filter(item => item.type === CandidateFilterType.TypeOfJob);
    filtersDto.jobPositionTitleFilters = filters.filter(item => item.type === CandidateFilterType.JobPositionTitleQueries);

    return filtersDto;
  }

  getTypeOfJobFilters(): FilterValue[] {
    return this.typeOfJobFilters;
  }

  getLocationFilters(): FilterValue[] {
    return this.locationFilters;
  }

  getPositionFilters(): FilterValue[] {
    return this.positionFilters;
  }
  getPositionFiltersNonPending(): FilterValue[] {
    return this.positionFilters.filter(positionFilter => !positionFilter.positionDetails?.isPending);
  }

  getFacilityFilters(): FilterValue[] {
    return this.facilityFilters;
  }

  getDepartmentFilters(): FilterValue[] {
    return this.departmentFilters;
  }

  getEmployerFilters(): FilterValue[] {
    return this.employerFilters;
  }

  getJobPositionTitleFilters(): FilterValue[] {
    return this.jobPositionTitleFilters;
  }

  getCombinedJobTitlesFilters(): FilterValue[] {
    return this.jobPositionTitleFilters.concat(this.positionFilters);
  }

  hasLocationFilters(): boolean {
    return this.locationFilters.length > 0;
  }

  hasPositionFilters(): boolean {
    return this.positionFilters.length > 0 || this.jobPositionTitleFilters.length > 0;
  }

  hasNoPendingPositions(): boolean {
    return (
      !this.hasPositionFilters() || this.positionFilters.filter(el => el.type === 'Position' && !el.positionDetails?.isPending).length > 0
    );
  }

  hasFacilityFilters(): boolean {
    return this.facilityFilters.length > 0;
  }

  hasDepartmentFilters(): boolean {
    return this.departmentFilters.length > 0;
  }

  hasEmployerFilters(): boolean {
    return this.employerFilters.length > 0;
  }

  hasJobPositionTitleFilters(): boolean {
    return this.jobPositionTitleFilters.length > 0;
  }

  getSelectedAddresses(): LocationObject[] {
    return this.getLocationFilters().map(filter => filter.locationObject as LocationObject);
  }

  hasFilters(): boolean {
    return (
      this.hasLocationFilters() ||
      this.hasPositionFilters() ||
      this.hasFacilityFilters() ||
      this.hasDepartmentFilters() ||
      this.hasEmployerFilters() ||
      this.hasJobPositionTitleFilters()
    );
  }

  canAddLocation(): boolean {
    return this.getLocationFilters().length < MAX_LOCATIONS_IN_PROFILE;
  }

  canAddDepartment(): boolean {
    return this.getDepartmentFilters().length < MAX_DEPARTMENTS_IN_PROFILE;
  }

  canAddJobPosition(): boolean {
    return this.getPositionFilters().length + this.getJobPositionTitleFilters().length < MAX_JOB_TITLE_ANSWERS;
  }

  canAddJobPositionPending(): boolean {
    return this.getPositionFiltersNonPending().length + this.getJobPositionTitleFilters().length < MAX_JOB_TITLE_ANSWERS;
  }
  cantRemoveLocation(): boolean {
    return !(this.getLocationFilters().length > 1);
  }

  cantRemovePosition(tryingToRemoveTitle: string): boolean {
    return this.getPositionFilters().length === 1 && this.getPositionFilters()[0].value === tryingToRemoveTitle;
  }

  facilityHasDepartmentsEnabled(): boolean {
    const facilities = this.getFacilityFilters();
    return (
      facilities.length > 0 &&
      hasCommonElement(
        facilities.map(item => item.value),
        TYPES_OF_WORK_WITH_DEPARTMENTS,
      )
    );
  }

  getFiltersLength(): number {
    return (
      this.getLocationFilters().length +
      this.getPositionFilters().length +
      this.getFacilityFilters().length +
      this.getDepartmentFilters().length +
      this.getEmployerFilters().length +
      this.getTypeOfJobFilters().length +
      this.getJobPositionTitleFilters().length
    );
  }

  getAllFilters: () => FilterValue[] = () => [
    ...this.getLocationFilters(),
    ...this.getPositionFilters(),
    ...this.getFacilityFilters(),
    ...this.getDepartmentFilters(),
    ...this.getEmployerFilters(),
    ...this.getTypeOfJobFilters(),
    ...this.getJobPositionTitleFilters(),
  ];

  isEmpty() {
    return !this.hasFilters() || !this.hasNoPendingPositions();
  }

  hasMissingRequiredFilters() {
    return !this.hasLocationFilters() || !this.hasPositionFilters();
  }
  hasMinimumFiltersToSyncProfile() {
    return this.hasLocationFilters() && this.hasPositionFilters();
  }

  getHighestPriorityFilterPositions(): CandidatePriority | undefined {
    const positions = this.positionFilters.filter(positionFilter => !positionFilter.positionDetails?.isPending);
    return minBy(positions, 'positionDetails.trackingPriority')?.positionDetails?.trackingPriority as CandidatePriority | undefined;
  }

  hasPositionsToGetPotentialJobs(previousValue: FiltersDto, candidatePriority?: CandidatePriority): boolean {
    const referencePriority = candidatePriority ?? this.getHighestPriorityFilterPositions();
    const currentPositionSet = differenceBy(this.positionFilters, previousValue.positionFilters, 'value');
    return (
      currentPositionSet.length > 0 &&
      currentPositionSet.every(({ positionDetails }) => {
        if (!positionDetails) return false;
        return !positionDetails?.isPending || (referencePriority && positionDetails?.trackingPriority >= referencePriority);
      })
    );
  }
}
